import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { defaultLimit, defaultOffset } from 'config/Api.Config';
import productCategoryDeleteApi from 'models/productManagement/productCategory/delete/Api.ProductCategory.Delete';
import ProductCategory from 'models/productManagement/productCategory/Model.ProductCategory';
import productCategorySaveApi from 'models/productManagement/productCategory/save/Api.ProductCategory.Save';
import { useMemo } from 'react';
import { productCategoryIndexRoute } from './ProductCategory.Index';

export enum ProductCategoryAction {
  EDIT = 'edit',
  DELETE = 'delete',
}
export type VerifyProductCategoryAction = Utils.VerifyExtends<
  Module.ProductManagement.ProductCategory.Actions,
  Utils.ValueOf<typeof ProductCategoryAction>
>;
export default function useProductCategory() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.ProductCategory.Params,
    Module.ProductManagement.ProductCategory.Query
  >({
    route: productCategoryIndexRoute,
  });

  const canView = permissions.CREATE_PRODUCT || permissions.EDIT_PRODUCT || permissions.DELETE_PRODUCT;
  const listApi = useApi({
    action: ProductCategory.list,
    default: { limit: defaultLimit, offset: defaultOffset },
    body: pageRouter.query.list,
    callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
    wait: !canView,
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const deleteApi = useApi({
    action: productCategoryDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const saveApi = useApi({
    action: productCategorySaveApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  return {
    canView,
    pageRouter,
    listApi,
    deleteApi,
    saveApi,
    selected,
    permissions,
    views: {
      [ProductCategoryAction.DELETE]:
        pageRouter.params.action === ProductCategoryAction.DELETE && selected && permissions.DELETE_PRODUCT,
      [ProductCategoryAction.EDIT]:
        pageRouter.params.action === ProductCategoryAction.EDIT &&
        ((permissions.CREATE_PRODUCT && !selected) || (permissions.EDIT_PRODUCT && selected)),
    },
  };
}

export function useProductCategoryProvider() {
  return useDataProvider<ReturnType<typeof useProductCategory>>();
}
